const ACTIVITY_KEY = 'wordpop-activity-tracking';
const MAX_EVENTS = 100;

export interface ActivityEvent {
  type: 'page_view' | 'feature_use' | 'game_action' | 'session';
  name: string;
  timestamp: string;
  metadata?: Record<string, string | number | boolean>;
}

export interface ActivitySummary {
  totalEvents: number;
  pageViews: number;
  featureUses: number;
  gameActions: number;
  sessions: number;
  topFeatures: { name: string; count: number }[];
  lastActivity: string | null;
}

function getStoredEvents(): ActivityEvent[] {
  try {
    const stored = localStorage.getItem(ACTIVITY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveEvents(events: ActivityEvent[]): void {
  try {
    // Keep only the most recent events
    const trimmed = events.slice(-MAX_EVENTS);
    localStorage.setItem(ACTIVITY_KEY, JSON.stringify(trimmed));
  } catch (e) {
    console.warn('Failed to save activity events:', e);
  }
}

export function trackActivity(
  type: ActivityEvent['type'],
  name: string,
  metadata?: ActivityEvent['metadata']
): void {
  const events = getStoredEvents();
  events.push({
    type,
    name,
    timestamp: new Date().toISOString(),
    metadata
  });
  saveEvents(events);
}

export function trackPageView(pageName: string): void {
  trackActivity('page_view', pageName);
}

export function trackFeatureUse(featureName: string, metadata?: Record<string, string | number | boolean>): void {
  trackActivity('feature_use', featureName, metadata);
}

export function trackGameAction(actionName: string, metadata?: Record<string, string | number | boolean>): void {
  trackActivity('game_action', actionName, metadata);
}

export function trackSession(action: 'start' | 'end'): void {
  trackActivity('session', action);
}

export function getActivitySummary(): ActivitySummary {
  const events = getStoredEvents();
  
  const pageViews = events.filter(e => e.type === 'page_view').length;
  const featureUses = events.filter(e => e.type === 'feature_use').length;
  const gameActions = events.filter(e => e.type === 'game_action').length;
  const sessions = events.filter(e => e.type === 'session' && e.name === 'start').length;

  // Calculate top features
  const featureCounts: Record<string, number> = {};
  events
    .filter(e => e.type === 'feature_use')
    .forEach(e => {
      featureCounts[e.name] = (featureCounts[e.name] || 0) + 1;
    });

  const topFeatures = Object.entries(featureCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const lastEvent = events[events.length - 1];

  return {
    totalEvents: events.length,
    pageViews,
    featureUses,
    gameActions,
    sessions,
    topFeatures,
    lastActivity: lastEvent?.timestamp || null
  };
}

export function clearActivityData(): void {
  localStorage.removeItem(ACTIVITY_KEY);
}

// Initialize session tracking
export function initActivityTracking(): void {
  trackSession('start');
  
  // Track page visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      trackSession('end');
    } else {
      trackSession('start');
    }
  });
}
